// src/components/employee/Dashboard.js - Updated with row click & terminology change
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Badge,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  ExitToApp as LogoutIcon,
  PhotoCamera as CameraIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const Dashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/jobs/my-jobs");
      setJobs(res.data.jobs);
      setError("");
    } catch (error) {
      setError("Failed to load jobs. Please try again.");
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleCaptureClick = (jobId) => {
    navigate(`/job/${jobId}/capture`);
  };

  const handleJobRowClick = (jobId) => {
    navigate(`/job/${jobId}`);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusChip = (status) => {
    let color = "default";

    switch (status) {
      case "pending":
        color = "warning";
        break;
      case "in-progress":
        color = "info";
        break;
      case "completed":
        color = "success";
        break;
      default:
        color = "default";
    }

    return (
      <Chip
        label={status.toUpperCase()}
        color={color}
        size="small"
        sx={{ textTransform: "capitalize" }}
      />
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            FieldTag.ai
          </Typography>
          {user && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ mr: 2 }}>
                Hi, {user.name}
              </Typography>
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" component="h1">
              My Assigned Jobs
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchJobs}
            >
              Refresh
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : jobs.length === 0 ? (
            <Alert severity="info">You don't have any assigned jobs yet.</Alert>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}
            >
              <Table stickyHeader aria-label="jobs table">
                <TableHead>
                  <TableRow>
                    <TableCell>Job Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job) => (
                    <TableRow
                      key={job._id}
                      hover
                      onClick={() => handleJobRowClick(job._id)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {job.name}
                      </TableCell>
                      <TableCell>{formatDate(job.date)}</TableCell>
                      <TableCell>{getStatusChip(job.status)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleJobRowClick(job._id);
                              }}
                              aria-label="view job details"
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Capture Equipment">
                            <IconButton
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleCaptureClick(job._id);
                              }}
                              aria-label="capture equipment data"
                            >
                              <CameraIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Dashboard;
